import React, { useState } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Autocomplete, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import { BrandLogo, BrandStandards } from '@marketing-milk/interfaces'
import { Dropzone } from '@marketing-milk/frontend'
import { useUploadBrandLogo } from 'hooks/api/useSprintPackets'
import { PacketModalShell } from '../../components/PacketModalShell'
import { radius, SurfacePalette, type } from '../../tokens'
import { brandFontStack, useBrandFonts } from '../../useBrandFonts'
import { FONT_SUGGESTIONS } from '../../googleFonts'

interface Props {
  palette: SurfacePalette
  brandStandards: BrandStandards | null
  brandLogos: BrandLogo[]
  saving?: boolean
  onSave: (brandStandards: BrandStandards) => void
  onClose: () => void
}

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const normalizeHex = (raw: string): string | null => {
  const match = HEX_RE.exec(raw.trim())
  return match ? `#${match[1].toLowerCase()}` : null
}

/**
 * Edit-only Brand Standards form: logo uploads (straight to the business's
 * private S3 folder — a logo only becomes part of the packet on Save), font
 * names as free-text rows (first is implicitly primary), and hex color rows.
 * Persists through the shared packet update path, which re-sanitizes
 * server-side.
 */
export const BrandStandardsEditor = ({
  palette,
  brandStandards,
  brandLogos,
  saving,
  onSave,
  onClose,
}: Props) => {
  const uploadBrandLogo = useUploadBrandLogo()
  const [logos, setLogos] = useState<BrandLogo[]>(brandLogos)
  const [fonts, setFonts] = useState<string[]>(brandStandards?.fonts ?? [])
  const [colors, setColors] = useState<string[]>(brandStandards?.colors ?? [])
  const [fontDraft, setFontDraft] = useState('')
  const [colorDraft, setColorDraft] = useState('#')
  const [uploading, setUploading] = useState(false)

  // Live-load already-entered fonts so the row previews render in-typeface.
  useBrandFonts(fonts)

  const onUpload = async (file: File) => {
    try {
      setUploading(true)
      const logo = await uploadBrandLogo(file)
      setLogos(prev => (prev.some(l => l.key === logo.key) ? prev : [...prev, logo]))
    } catch (err) {
      toast.error(`Logo upload failed: ${err}`)
    } finally {
      setUploading(false)
    }
  }

  const addFont = (raw?: string) => {
    const name = (raw ?? fontDraft).trim()
    if (!name) return
    setFonts(prev => (prev.includes(name) ? prev : [...prev, name]))
    setFontDraft('')
  }

  const addColor = () => {
    const hex = normalizeHex(colorDraft)
    if (!hex) {
      toast.error('Enter a hex color like #a044ff')
      return
    }
    setColors(prev => (prev.includes(hex) ? prev : [...prev, hex]))
    setColorDraft('#')
  }

  const save = () => onSave({ logos: logos.map(l => l.key), fonts, colors })

  return (
    <PacketModalShell
      palette={palette}
      title="Brand standards"
      subtitle="The client's logos, fonts, and colors — shown read-only when presenting."
      onClose={onClose}
      maxWidth={640}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          maxHeight: '60vh',
          overflowY: 'auto',
        }}
      >
        <div>
          <FieldLabel palette={palette}>Logos</FieldLabel>
          {logos.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {logos.map(logo => (
                <div
                  key={logo.key}
                  style={{
                    position: 'relative',
                    background: '#ffffff',
                    border: `1px solid ${palette.hairline}`,
                    borderRadius: radius.sm,
                    padding: 12,
                    width: 148,
                    height: 96,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={logo.url}
                    alt="Brand logo"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                  <button
                    type="button"
                    aria-label="Remove logo"
                    onClick={() => setLogos(prev => prev.filter(l => l.key !== logo.key))}
                    style={removeBtn(palette)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {uploading ? (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, color: palette.textMuted }}
            >
              <CircularProgress size={18} style={{ color: palette.accent }} />
              <span style={{ fontSize: type.size.body }}>Uploading…</span>
            </div>
          ) : (
            <Dropzone
              allowedExtensions={['.png', '.jpg', '.jpeg', '.svg', '.webp', '.gif']}
              handleUpload={onUpload}
            />
          )}
        </div>

        <div>
          <FieldLabel palette={palette}>Fonts</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fonts.map((font, i) => (
              <div key={font} style={row(palette)}>
                <span
                  style={{
                    flex: 1,
                    fontFamily: brandFontStack(font, type.family.sans),
                    fontSize: type.size.bodyLg,
                    color: palette.text,
                  }}
                >
                  {font}
                </span>
                {i === 0 && (
                  <span style={{ fontSize: type.size.eyebrow, color: palette.textFaint }}>
                    Primary
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${font}`}
                  onClick={() => setFonts(prev => prev.filter(f => f !== font))}
                  style={removeBtnInline(palette)}
                >
                  ×
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <Autocomplete
                freeSolo
                options={FONT_SUGGESTIONS.filter(f => !fonts.includes(f))}
                inputValue={fontDraft}
                onInputChange={(_, value) => setFontDraft(value)}
                // Selecting a suggestion adds it immediately; free-typed names
                // are added via Enter/Add so half-typed drafts don't commit.
                onChange={(_, value) => {
                  if (typeof value === 'string' && value) addFont(value)
                }}
                sx={{ flex: 1 }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search Google Fonts or type any font name"
                    onKeyDown={e => {
                      if (e.key === 'Enter') addFont()
                    }}
                    inputProps={{ ...params.inputProps, 'aria-label': 'Add font' }}
                  />
                )}
              />
              <button type="button" onClick={() => addFont()} style={ghostBtn(palette)}>
                Add
              </button>
            </div>
            <p style={{ margin: 0, fontSize: type.size.eyebrow, color: palette.textFaint }}>
              The first font is treated as primary. Google Fonts render in their own typeface;
              custom fonts are recorded by name.
            </p>
          </div>
        </div>

        <div>
          <FieldLabel palette={palette}>Colors</FieldLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {colors.length > 0 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {colors.map(color => (
                  <div key={color} style={{ ...row(palette), gap: 8, padding: '6px 8px' }}>
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        background: color,
                        border: `1px solid ${palette.hairline}`,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: type.family.mono,
                        fontSize: type.size.eyebrow,
                        color: palette.textMuted,
                      }}
                    >
                      {color}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${color}`}
                      onClick={() => setColors(prev => prev.filter(c => c !== color))}
                      style={removeBtnInline(palette)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={normalizeHex(colorDraft) ?? '#a044ff'}
                onChange={e => setColorDraft(e.target.value)}
                aria-label="Pick color"
                style={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={colorDraft}
                onChange={e => setColorDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addColor()}
                placeholder="#a044ff"
                aria-label="Add color hex"
                style={{ ...textInput(palette), maxWidth: 140, fontFamily: type.family.mono }}
              />
              <button type="button" onClick={addColor} style={ghostBtn(palette)}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button type="button" onClick={onClose} style={ghostBtn(palette)}>
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || uploading}
          style={primaryBtn(palette, !!saving || uploading)}
        >
          {saving ? 'Saving…' : 'Save brand standards'}
        </button>
      </div>
    </PacketModalShell>
  )
}

const FieldLabel = ({
  palette,
  children,
}: {
  palette: SurfacePalette
  children: React.ReactNode
}) => (
  <div
    style={{
      fontFamily: type.family.mono,
      fontSize: type.size.eyebrow,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: palette.textMuted,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
)

const row = (palette: SurfacePalette): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 12px',
  borderRadius: radius.sm,
  border: `1px solid ${palette.hairline}`,
  background: palette.surface,
})

const textInput = (palette: SurfacePalette): React.CSSProperties => ({
  flex: 1,
  padding: '8px 12px',
  borderRadius: radius.sm,
  border: `1px solid ${palette.hairline}`,
  background: palette.surface,
  color: palette.text,
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  outline: 'none',
})

const removeBtn = (palette: SurfacePalette): React.CSSProperties => ({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 20,
  height: 20,
  lineHeight: '18px',
  padding: 0,
  borderRadius: 999,
  border: `1px solid ${palette.hairline}`,
  background: palette.surface,
  color: palette.textMuted,
  fontSize: 13,
  cursor: 'pointer',
})

const removeBtnInline = (palette: SurfacePalette): React.CSSProperties => ({
  border: 'none',
  background: 'transparent',
  color: palette.textFaint,
  fontSize: 16,
  lineHeight: 1,
  padding: '0 2px',
  cursor: 'pointer',
})

const ghostBtn = (palette: SurfacePalette): React.CSSProperties => ({
  border: `1px solid ${palette.hairline}`,
  background: 'transparent',
  color: palette.textMuted,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  cursor: 'pointer',
})

const primaryBtn = (palette: SurfacePalette, disabled: boolean): React.CSSProperties => ({
  border: `1px solid ${palette.accent}`,
  background: palette.accent,
  color: palette.accentInk,
  borderRadius: radius.sm,
  padding: '8px 16px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  fontWeight: type.weight.medium,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.6 : 1,
})
